import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useTranslation } from "react-i18next";
import {
  primaryColor,
  secondaryColor,
  tooltipContentStyle,
  tooltipCursorStyle,
} from "utils/dashboardUtils";
import { formatLabel, isDesktop } from "utils/utils";
import DashboardFilter from "../DashboardFilter";

const CustomizedAxisTick = ({ x, y, payload, activeFilterType }) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill={secondaryColor}
        fontSize={11}
        fontWeight={500}
        transform="rotate(-35)"
      >
        {formatLabel(payload.value, activeFilterType)}
      </text>
    </g>
  );
};

const MembershipGrowthChart = ({
  membershipGrowth,
  activeFilterType,
  className = "",
  showLeadsPageFilter = false,
  dates,
  setDates,
  setActiveFilter
}) => {
  const { t } = useTranslation();

  const MEMBERSHIP_GROWTH_DATA = membershipGrowth?.map((item) => ({
    month: item.label,
    total: item.newMembers,
    signup: item.newSignups,
  }));

  const getSubtitle = () => {
    switch (activeFilterType) {
      case "DAY":
        return t("Total active members over the last 24 hours");
      case "WEEK":
        return t("Total active members this week");
      case "MONTH":
        return t("Total active members over the last 30 days");
      case "YEAR":
        return t("Total active members over the last 12 months");
      default:
        return t("Total active members");
    }
  };
  const shouldRotate = !isDesktop || MEMBERSHIP_GROWTH_DATA?.length > 6;

  return (
    <div className={`membership_growth dashboard_common_card ${className}`}>
      {/* Title */}
      <div className="d-flex justify-content-between align-items-center mb-5 flex-wrap membership_stat">
        <div>
          <h3 className="fs_20">{showLeadsPageFilter ? t("Leads Growth") :t("Membership Growth")}</h3>
          <p className="page_sub_title mt-2">{getSubtitle()}</p>
        </div>
        {showLeadsPageFilter && (
          <DashboardFilter
            dates={dates}
            setDates={setDates}
            setActiveFilter={setActiveFilter}
            className={"leads_stats mb-0"}
            selectDateLabel={false}
            selectTypeLabel={false}
          />
        )}
        {/* Legend */}
        <div className="legends">
          <div className="inner_legend">
            <span className="dot total_dot" />
            {t("New Members")}
          </div>
          <div className="inner_legend">
            <span className="dot new_dot" />
            {t("New Signups")}
          </div>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={MEMBERSHIP_GROWTH_DATA}
          margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
          barCategoryGap="30%"
          barGap={4}
        >
          <XAxis
            dataKey="month"
            axisLine={false}
            tickLine={false}
            interval={isDesktop ? 0 : 1}
            height={shouldRotate ? 60 : 30}
            tick={
              shouldRotate
                ? (props) => (
                    <CustomizedAxisTick
                      {...props}
                      activeFilterType={activeFilterType}
                    />
                  )
                : { fill: secondaryColor, fontSize: 12, fontWeight: 500 }
            }
            tickMargin={shouldRotate ? 0 : 15}
            tickFormatter={
              shouldRotate
                ? undefined
                : (label) => formatLabel(label, activeFilterType)
            }
          />

          <YAxis hide />

          <Tooltip
            cursor={tooltipCursorStyle}
            formatter={(value, name) => [
              value,
              name === "total" ? t("New Members") : t("New Signups"),
            ]}
            labelFormatter={(label) => formatLabel(label, activeFilterType)}
            contentStyle={tooltipContentStyle}
            labelStyle={{ fontWeight: 600 }}
          />

          <CartesianGrid
            vertical={false}
            stroke="#E6EAF0"
            strokeDasharray="0"
          />

          <Bar dataKey="total" fill={primaryColor} radius={[4, 4, 0, 0]} />

          <Bar dataKey="signup" fill={secondaryColor} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MembershipGrowthChart;
